import type { ListingScoreLikertLevel, PdpOrderBy, SortDirection } from '@vizit/api';
import type { RetailerKey } from '@vizit/constants';
import { ScoreLevel } from '@vizit/organisms';

/**
 * Enum representing the available action types for the product table filter reducer
 */
enum ProductTableActionType {
  SET_BRAND = 'SET_BRAND',
  SET_PRODUCT_CATEGORY_ID = 'SET_PRODUCT_CATEGORY_ID',
  SET_LISTING_SCORE_LEVEL = 'SET_LISTING_SCORE_LEVEL',
  SET_SEARCH_TERM = 'SET_SEARCH_TERM',
  SET_LIMIT = 'SET_LIMIT',
  SET_SORT_BY = 'SET_SORT_BY',
  SET_SORT_DIRECTION = 'SET_SORT_DIRECTION',
  SET_RETAILER = 'SET_RETAILER',
}

interface ProductTableState {
  /**
   * Number of items to fetch per page
   *
   * @optional
   * @type {number}
   * @default 20
   */
  limit?: number;
  /**
   * Sort field for the products
   *
   * @optional
   * @type {PdpOrderBy['field']}
   * @default 'created_at'
   */
  sortBy?: PdpOrderBy['field'];
  /**
   * Sort direction for the products
   *
   * @optional
   * @type {'asc' | 'desc'}
   * @default 'desc'
   */
  sortDirection?: SortDirection;
  /**
   * The brand to filter by
   * @optional
   * @type {string}
   */
  brand?: string;
  /**
   * The product category ID to filter by
   * @optional
   * @type {string}
   */
  productCategoryId?: string | null;
  /**
   * The product category name to filter by
   * @optional
   * @type {string}
   */
  productCategoryName?: string;
  /**
   * The listing score level to filter by
   * @optional
   * @type {ScoreLevel}
   */
  listingScoreLevel?: ScoreLevel;
  /**
   * Unified search term (searches name OR item_id)
   * @optional
   * @type {string}
   */
  searchTerm?: string;
  /**
   * The retailer key to filter by (e.g. 'amazon_us', 'walmart_us')
   * @optional
   * @type {RetailerKey}
   */
  retailer?: RetailerKey;
}

/**
 * Default initial state for the product table filters
 */
const DEFAULT_FILTER_STATE: ProductTableState = {
  brand: undefined,
  productCategoryId: null,
  productCategoryName: '',
  listingScoreLevel: ScoreLevel.All,
  searchTerm: '',
  retailer: undefined,
  limit: 20,
  sortBy: 'created_at',
  sortDirection: 'desc',
};

interface TransformedQueryParams {
  limit?: number;
  sort_by?: PdpOrderBy['field'];
  sort_direction?: SortDirection;
  brand?: string[];
  product_category_id?: string[];
  listing_score_level?: ListingScoreLikertLevel[];
  search?: string;
  retailer?: string[];
}

/**
 * Transforms the product table state into query parameters for the API
 * @param state - The current product table state
 * @returns Query parameters object for the search PDPs API
 */
function transformStateToQueryParams(state: ProductTableState): TransformedQueryParams {
  const { limit, sortBy, sortDirection, brand, productCategoryId, listingScoreLevel, searchTerm, retailer } = state;

  return {
    limit: limit ?? DEFAULT_FILTER_STATE.limit,
    sort_by: sortBy ?? DEFAULT_FILTER_STATE.sortBy,
    sort_direction: sortDirection ?? DEFAULT_FILTER_STATE.sortDirection,
    brand: !brand || brand === DEFAULT_FILTER_STATE.brand ? undefined : [brand],
    product_category_id:
      !productCategoryId || productCategoryId === DEFAULT_FILTER_STATE.productCategoryId
        ? undefined
        : [productCategoryId],
    listing_score_level:
      !listingScoreLevel || listingScoreLevel === DEFAULT_FILTER_STATE.listingScoreLevel
        ? undefined
        : ([listingScoreLevel] as ListingScoreLikertLevel[]),
    search: !searchTerm || searchTerm === DEFAULT_FILTER_STATE.searchTerm ? undefined : searchTerm,
    retailer: retailer ? [retailer] : undefined,
  };
}

/**
 * Reducer function for managing product table filter state
 * @param currentState - The current state
 * @param action - The action to apply to the state
 * @returns The new state after applying the action
 */
function productTableFilterReducer(
  currentState: ProductTableState,
  action: { type: ProductTableActionType; payload: Partial<ProductTableState> },
) {
  const {
    limit,
    sortBy,
    sortDirection,
    brand,
    productCategoryId,
    productCategoryName,
    listingScoreLevel,
    searchTerm,
    retailer,
  } = action.payload ?? {};

  switch (action.type) {
    case ProductTableActionType.SET_BRAND:
      return { ...currentState, brand };
    case ProductTableActionType.SET_PRODUCT_CATEGORY_ID:
      return { ...currentState, productCategoryId, productCategoryName };
    case ProductTableActionType.SET_LISTING_SCORE_LEVEL:
      return { ...currentState, listingScoreLevel };
    case ProductTableActionType.SET_SEARCH_TERM:
      return { ...currentState, searchTerm };
    case ProductTableActionType.SET_LIMIT:
      return { ...currentState, limit };
    case ProductTableActionType.SET_SORT_BY:
      return { ...currentState, sortBy };
    case ProductTableActionType.SET_SORT_DIRECTION:
      return { ...currentState, sortDirection };
    case ProductTableActionType.SET_RETAILER:
      return { ...currentState, retailer };
    default:
      return currentState;
  }
}

export { ProductTableActionType, productTableFilterReducer, transformStateToQueryParams, DEFAULT_FILTER_STATE };
export type { ProductTableState };
